import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { currency, fmtDate } from "../../lib/status";

export default function TechEarnings() {
    const { user } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [payouts, setPayouts] = useState([]);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/bookings");
            setBookings(data);
            const { data: p } = await api.get("/payouts");
            setPayouts(p);
        })();
    }, []);

    const completed = bookings.filter((b) =>
        ["payment_completed", "invoice_generated", "customer_confirmed", "job_completed"].includes(b.status),
    );
    const totalEarned = completed.reduce(
        (s, b) => s + (b.technician_payout || 0),
        0,
    );
    const pendingPayout = payouts
        .filter((p) => p.status === "pending")
        .reduce((s, p) => s + (p.net_amount || 0), 0);
    const paidPayout = payouts
        .filter((p) => p.status === "paid")
        .reduce((s, p) => s + (p.net_amount || 0), 0);

    return (
        <div className="space-y-5">
            <div className="lx-label text-[#B2912A]">Earnings</div>
            <h1 className="font-sans font-bold text-3xl text-[#0E0E0E] tracking-tight">Your money</h1>

            <div
                className="p-7 text-white relative overflow-hidden"
                style={{
                    background: "linear-gradient(135deg,#180A21 0%, #2B1438 70%, #180A21 100%)",
                }}
            >
                <div className="absolute -right-10 -bottom-10 w-60 h-60 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="relative z-10">
                    <div className="lx-label text-[#D4AF37]">Lifetime earnings</div>
                    <div className="font-serif text-5xl text-[#D4AF37] mt-2">
                        {currency(totalEarned, "AED")}
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-white/10">
                        <div>
                            <div className="lx-label text-white/60">Pending</div>
                            <div className="font-serif text-2xl">
                                {currency(pendingPayout, "AED")}
                            </div>
                        </div>
                        <div>
                            <div className="lx-label text-white/60">Paid out</div>
                            <div className="font-serif text-2xl">
                                {currency(paidPayout, "AED")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white">
                <div className="p-4 border-b border-[#F3F2F0] lx-label text-[#B2912A]">
                    Recent completed jobs
                </div>
                {completed.length === 0 ? (
                    <div className="p-8 text-center text-[#555]">
                        No completed jobs yet.
                    </div>
                ) : (
                    <div className="divide-y divide-[#F3F2F0]">
                        {completed.slice(0, 20).map((b) => (
                            <div key={b.id} className="p-4 flex justify-between items-center">
                                <div>
                                    <div className="text-sm text-[#180A21]">{b.service?.name}</div>
                                    <div className="text-xs text-[#555] mt-0.5">
                                        {b.booking_number} · {fmtDate(b.scheduled_at)}
                                    </div>
                                </div>
                                <div className="font-serif text-lg text-[#34C759]">
                                    +{currency(b.technician_payout, b.currency)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="bg-white">
                <div className="p-4 border-b border-[#F3F2F0] lx-label text-[#B2912A]">
                    Payouts
                </div>
                {payouts.length === 0 ? (
                    <div className="p-8 text-center text-[#555]">No payouts yet.</div>
                ) : (
                    <div className="divide-y divide-[#F3F2F0]">
                        {payouts.map((p) => (
                            <div key={p.id} className="p-4 flex justify-between items-center">
                                <div>
                                    <div className="lx-label text-[#180A21]">{p.status}</div>
                                    <div className="text-xs text-[#555] mt-0.5">
                                        {p.booking_ids?.length} jobs · {fmtDate(p.period_end)}
                                    </div>
                                </div>
                                <div className="font-serif text-lg text-[#180A21]">
                                    {currency(p.net_amount, p.currency)}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
